<template>
  <b-modal
    id="create-ccf"
    :title="titleText"
    header-class="create-wizard-header"
    title-class="create-label"
    footer-class="create-wizard-footer"
    no-close-on-esc
    @shown="fetchInitData"
    @hidden="clearForm"
  >
    <div class="wizard-container">
      <div class="content-wrapper">
        <div class="wizard-form">
          <div class="ncm-row">
            <label class="ncm-column-left" for="cff-app-name">
              {{ nameText }}
            </label>
            <div class="ncm-column-right m-name">
              <text-input
                id="cff-app-name"
                v-model="appName"
                type="text"
                @on-text-input-change="onAppNameInputChanged"
              />
            </div>
          </div>

          <element-description
            :label="descriptionText"
            :outer-container-class="'ncm-row'"
            :label-class="'ncm-column-left'"
            :value-container-class="'ncm-column-right'"
            :elem-description="appDescription"
            @on-element-description-changed="onDeDescriptionChanged"
          ></element-description>

          <div class="ncm-row">
            <label
              class="ncm-column-left field-label-high"
              :class="{ 'field-label-low': uploadedFile }"
            >
              {{ fileText }}
            </label>
            <div class="ncm-column-right">
              <file-upload @on-file-change="onFileUpload"></file-upload>
            </div>
          </div>

          <div class="ncm-row">
            <label class="ncm-row-left">
              {{ this.$t("pageType") }}
              <a :href="helpDocLink" target="_blank">
                <svg class="info-icon">
                  <use
                    xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#info_alt"
                  ></use>
                </svg>
              </a>
            </label>
            <div class="ncm-column-right">
              <b-form-select
                v-if="!fetchAppTypeFailed"
                id="wizard-app-type"
                v-model="appType"
                :options="contentTypeText()"
                @change="onTypeChange"
                size="sm"
              ></b-form-select>
              <div class="loader-wrapper" v-if="isAppTypeLoading">
                <div class="loader"></div>
              </div>
              <label v-if="!isAppTypeLoading && fetchAppTypeFailed">
                {{ $t("noAvailablePageTypes") }}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-slot:modal-footer>
      <button class="modal-btn modal-cancel-btn" @click="clearForm">
        {{ getMessageText(messages.CANCEL) }}
      </button>
      <button
        class="modal-btn modal-save-btn"
        @click="doCreate"
        :disabled="shouldBeDisabled"
      >
        {{ getMessageText(messages.OK) }}
      </button>
    </template>
  </b-modal>
</template>

<script>
  import { mapState } from "vuex";
  import FileUpload from "@/components/FileUpload";
  import TextInput from "@/components/TextInput";
  import store from "@/store";
  import {
    HELPER_DOCS,
    MUTATION_NAME,
    TOAST_CONFIG
  } from "@/static/global-variables";
  import ElementDescription from "@/components/ElementDescription";
  import services from "@/services";
  import utils from "@/utils";
  import { QUERIES } from "@/static/queries";

  export default {
    name: "NewCustomContentFromFileModal",
    components: {
      TextInput,
      FileUpload,
      ElementDescription
    },
    data() {
      return {
        appName: "",
        isAppTypeLoading: true,
        uploadedFile: null,
        fileIsValid: false,
        isNewTemplate: false,
        selectedTemplate: null,
        appDescription: null,
        appType: "",
        types: []
      };
    },
    computed: {
      ...mapState({
        messages: "messages"
      }),
      titleText() {
        return this.$t("newCustomPageFromFile");
      },
      nameText() {
        return this.$t("pageName");
      },
      fileText() {
        return this.$t("file");
      },
      shouldBeDisabled() {
        return !this.appName || !this.uploadedFile || !this.fileIsValid;
      },
      descriptionText() {
        return this.getMessageText(this.messages.DESCRIPTION);
      },
      typeText() {
        return this.getMessageText(this.messages.CONTENT_TYPE);
      },
      helpDocLink() {
        return HELPER_DOCS.createContentLink;
      },
      fetchAppTypeFailed() {
        return !this.types.length;
      }
    },
    methods: {
      clearForm() {
        this.appName = "";
        this.appDescription = null;
        this.appType = "";
        this.uploadedFile = null;
        this.fileIsValid = null;
        this.$bvModal.hide("create-ccf");
      },
      onDeDescriptionChanged(input) {
        this.appDescription = input;
      },
      onFileUpload(val) {
        this.fileIsValid = false;
        if (val !== null) {
          services.validateCustomContentFromFile(val.file).then(resp => {
            if (resp) {
              this.fileIsValid = true;
            } else {
              this.fileIsValid = false;
              this.displayErrorMessage(this.$t("indexHtmlMissing"));
            }
          });
          this.uploadedFile = val.file;
        }
      },
      displayErrorMessage(message) {
        this.$bvToast.toast(message, TOAST_CONFIG.BOTTOM_CENTER_ERROR);
      },
      errorHandler() {
        const component = this;
        const saveError = component.getMessageText(
          component.messages.SAVE_ERROR
        );
        this.displayErrorMessage(saveError);
      },
      navigateToEditPage(app) {
        this.$router.push({
          name: "Deploy",
          params: {
            appId: app.uuid,
            appName: app.name,
            type: app.type,
            testRecord: app.testRecord
          }
        });
      },
      doCreate() {
        const name = this.appName;
        const description = this.appDescription;
        services
          .createCustomContentWithFile(
            name,
            description,
            this.uploadedFile,
            this.appType.type
          )
          .then(resp => {
            if (!resp) {
              this.errorHandler();
              return;
            }
            store.commit(MUTATION_NAME.ADD_APP, resp);
            this.navigateToEditPage(resp);
          }, this.errorHandler)
          .catch(this.errorHandler);
        this.clearForm();
      },
      onAppNameInputChanged(val) {
        this.appName = val;
      },
      onTypeChange(val) {
        this.searchResults = [];
        const labels = store.getters.objectNameLabels;
        const mapping = utils.getRecordTypeObject(val.type);
        this.shouldHaveTestRecord = !!mapping;
        this.$refs.searchInputInCreate.closeSelected();

        if (mapping) {
          this.searchBoxLabel = utils.getVeevaMessageFromTemplate(
            this.getMessageText(this.messages.TEST_RECORD),
            labels[mapping.object].singular,
            "Test " // need to be default English
          );
          this.searchable = true;
        } else {
          this.searchable = false;
        }
      },
      fetchInitData() {
        this.fetchObjectLabel();
      },
      fetchObjectLabel() {
        if (!store.getters.objectNameLabels) {
          let result = {};
          services
            .fetchObjectNames()
            .then(resp => {
              result = {
                Account: resp.Account,
                KOL: resp.Account,
                Account_Plan_vod__c: resp.Account_Plan_vod__c,
                Order_vod__c: resp.Order_vod__c,
                Inventory_Monitoring_vod__c: resp.Inventory_Monitoring_vod__c,
                EM_Speaker_vod__c: resp.EM_Speaker_vod__c
              };
            })
            .catch(err => {
              console.error(err);
            })
            .finally(() => {
              store.commit(MUTATION_NAME.SET_OBJECT_NAME_LABELS, result);
              this.fetchAppTypes();
            });
        } else {
          this.fetchAppTypes();
        }
      },

      async fetchAppTypes() {
        this.isAppTypeLoading = true;
        if (store.getters.appTypes) {
          this.types = store.getters.appTypes;
          this.isAppTypeLoading = false;
          return;
        }

        let describeObjects = null;
        try {
          describeObjects = await services.fetchObjectOLS();
        } catch (error) {
          this.types = [];
          this.handleFetchAppTypeError();
          this.isAppTypeLoading = false;
          return;
        }

        services
          .fetchAppTypes()
          .then(resp => {
            this.fetchAppTypesSuccessHandler(resp, describeObjects);
          })
          .catch(() => {
            this.types = [];
            this.handleFetchAppTypeError();
            this.isAppTypeLoading = false;
          });
      },

      fetchAppTypesSuccessHandler(resp, describeObjects) {
        if (!resp || !Array.isArray(resp.records)) {
          this.types = [];
          this.handleFetchAppTypeError();
          this.isAppTypeLoading = false;
          return;
        }
        const objectMap = new Map(
          Object.entries(store.getters.objectNameLabels)
        );
        for (let label of objectMap.keys()) {
          if (!describeObjects[label] || !describeObjects[label].createable)
            objectMap.delete(label);
        }
        const recordTypes = utils.reformatRecordTypes(resp.records, objectMap);
        this.types = utils
          .filterTypes(recordTypes, describeObjects[QUERIES.htmlReport.object])
          .map(element => {
            element.text = element.value.type;
            return element;
          })
          .sort((a, b) => {
            return a.text.localeCompare(b.text);
          });
        store.commit(MUTATION_NAME.SET_APPTYPES, this.types);
        this.isAppTypeLoading = false;
      },

      handleFetchAppTypeError() {
        this.displayErrorMessage(
          this.getMessageText(this.messages.SFDC_METADATA_ERROR)
        );
      },

      contentTypeText() {
        return this.types.map(function(element) {
          return {
            text: this.$t(utils.camelCase(element.text)),
            value: element.value
          };
        }, this);
      }
    }
  };
</script>

<style scoped>
  .modal-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 0;
    background: transparent;
    background-clip: border-box;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    line-height: 1.875rem;
    color: #0070d2;
    -webkit-appearance: none;
    white-space: normal;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    text-decoration: none;
  }

  .modal-save-btn {
    margin-left: 0.5rem;
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    background-color: #0070d2;
    border: 1px solid #0070d2;
    color: #fff;
  }

  .modal-cancel-btn {
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    border: 1px solid #dddbda;
    background-color: #fff;
  }

  .modal-btn[disabled] {
    background-color: var(--DISABLED_BTN_GREY);
    border-color: var(--DISABLED_BTN_GREY);
    color: var(--WHITE);
  }

  .field-label-high {
    padding-top: 0.75rem;
  }

  .field-label-low {
    padding-top: 0.125rem;
  }

  .ncm-row-left {
    flex-direction: row;
  }

  .info-icon {
    width: 1rem;
    height: 1rem;
    margin-left: 0.25rem;
    fill: var(--TEXT_GREY_LIGHT);
  }

  .loader-wrapper {
    position: static;
  }
</style>
